/** Prefix a quote so a cell cannot be read as a spreadsheet formula. */
function neutralizeFormula(value: string): string {
  if (value.length > 0 && "=+-@\t\r".includes(value[0])) return "'" + value
  return value
}

/** Build a CSV (quoting fields that need it) and trigger a browser download. */
export function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = neutralizeFormula(String(v ?? ""))
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
  }
  const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n")
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Minimal CSV parser that respects double-quoted fields (tags contain commas).
 *
 * Headers come back LOWERCASED, so a caller looks a column up case-insensitively.
 * Shared by the waitlist import and the studios import - two parsers would mean
 * two sets of quoting bugs to find. */
export function parseCsv(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const grid: string[][] = []
  let field = "", row: string[] = [], inQuotes = false
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (inQuotes) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++ } else inQuotes = false }
      else field += c
    } else if (c === '"') inQuotes = true
    else if (c === ",") { row.push(field); field = "" }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++
      row.push(field); field = ""
      if (row.some((f) => f.trim() !== "")) grid.push(row)
      row = []
    } else field += c
  }
  if (field !== "" || row.length) { row.push(field); if (row.some((f) => f.trim() !== "")) grid.push(row) }
  if (grid.length === 0) return { headers: [], rows: [] }
  const headers = grid[0].map((h) => h.replace(/^\uFEFF/, "").trim())
  const lower = headers.map((h) => h.toLowerCase())
  const rows = grid.slice(1).map((r) => {
    const obj: Record<string, string> = {}
    lower.forEach((h, idx) => (obj[h] = (r[idx] ?? "").trim()))
    return obj
  })
  return { headers: lower, rows }
}
